import { ConfigProvider, ThemeConfig } from 'antd';
import { GetServerSideProps } from 'next';
import { Inter } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { removeTrailingSlash } from '@/utils/removeTrailingSlash';

import { metaTagProps } from '..';
import SchoolDetailsPage from '../../../components/ResourcesPage/SchoolsPage/SchoolDetailsPage';
import { formateErrorObject } from '../../../components/shared/formateErrorObject';
import CustomLoader from '../../../components/shared/Loading';
import { notify } from '../../../components/shared/notify';
import { getBlogSingle, getMetaTagSchool } from '../../../services/blog';

const config: ThemeConfig = {
  token: {
    colorPrimary: 'white',
    fontFamily: 'inherit',
  },
};

const inter = Inter({
  subsets: ['latin'],
  display: 'swap',
  fallback: ['sans-serif'],
});

const SchoolDetails: React.FC<metaTagProps> = ({
  metaData,
  canonicalUrlPageUrl,
}) => {
  const [data, setData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { schoolId } = router.query;

  const handleGetSingleSchool = async () => {
    if (!schoolId) return;
    try {
      setLoading(true);
      const response = await getBlogSingle(schoolId as string);
      const { data } = response;
      setData(data);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
      if (message === 'School Not Found') {
        router.replace('/404');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (schoolId) {
      handleGetSingleSchool();
    }
  }, [schoolId]);

  const transformData = useMemo(() => {
    const posts = data?.posts.map((post: any) => ({
      number: post.schoolId,
      title: post.title,
      description: post?.description,
      slug: post?.slug,
      postId: post?.id,
      schoolId: post?.school_id,
      reading_history: post?.reading_history,
      postImage: post?.documents?.file_path,
      reading_time: post?.reading_time,
    }));

    return {
      imageSrc: data?.documents?.file_path || '',
      candidateImageSrc: data?.documents?.file_path || '',
      candidateName: data?.author_profile?.name,
      title: data?.name,
      description: data?.description,
      downloadPdfButtonText: '',
      schoolId: data?.id,
      created_at: data?.created_at,
      slug: data?.slug,
      chapters: posts,
      last_read_post_slug: data?.last_read_post_slug || '',
      categoriesId: data?.categories?.[0]?.id,
      categoriesName: data?.categories?.[0]?.name,
      authorUserName: data?.author_profile?.user_name,
      authorAvatar: data?.author_profile?.avatar,
      reading_time: data?.reading_time,
      authorData: {
        name: data?.author_profile?.name,
        image: '',
        readersCount: data?.author?.follower_count,
        authorId: data?.author?.zl_uid,
        isFollowing: data?.followed,
        authorUserName: data?.author_profile?.user_name,
        authorAvatar: data?.author_profile?.avatar,
      },
    };
  }, [data]);

  return (
    <div className={inter.className}>
      <ConfigProvider theme={config}>
        <Head>
          <title>{(metaData?.title ?? metaData?.name) || ''}</title>
          <meta
            name='title'
            content={(metaData?.title ?? metaData?.name) || ''}
          />
          <meta
            name='description'
            content={
              (metaData?.meta_description ?? metaData?.description)?.slice(
                0,
                160,
              ) || ''
            }
            key='description'
          />
          <meta name='author' content={metaData?.author || 'Zebralearn'} />
          {metaData?.keywords && (
            <meta name='keywords' content={metaData?.keywords} />
          )}

          {/* <!-- Open Graph tags --> */}

          <meta property='og:site_name' content='ZebraLearn' />
          <meta property='og:locale' content='en_IN' />
          <meta
            property='og:title'
            content={(metaData?.title ?? metaData?.name) || ''}
            key='ogtitle'
          />
          <meta
            property='og:description'
            content={
              (metaData?.meta_description ?? metaData?.description)?.slice(
                0,
                160,
              ) || ''
            }
            key='ogdesc'
          />
          <meta property='og:type' content='website' key='website' />
          <meta
            property='og:url'
            content={removeTrailingSlash(
              metaData?.canonical ?? (canonicalUrlPageUrl as string),
            )}
            key='ogurl'
          />
          <meta
            property='og:image'
            content={
              `${process.env.NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS}/${metaData?.documents?.file_path}` ||
              'https://zebralearn-store.s3.ap-south-1.amazonaws.com/thubnail-creative.png'
            }
            key='ogimage'
          />
          <meta property='og:image:alt' content='Zebralearn Schools' />
          <meta property='og:image:type' content='image/png' />
          <meta property='og:image:width' content='1200' key='ogimagewidth' />
          <meta property='og:image:height' content='630' key='ogimageheight' />

          <link
            rel='canonical'
            href={removeTrailingSlash(
              metaData?.canonical ?? (canonicalUrlPageUrl as string),
            )}
            key='canonical'
          />

          <meta name='twitter:card' content='summary_large_image' />
          <meta property='twitter:domain' content='zebralearn.com' />
          <meta
            property='twitter:url'
            content={removeTrailingSlash(canonicalUrlPageUrl as string)}
          />
          <meta
            name='twitter:title'
            content={(metaData?.title ?? metaData?.name) || ''}
          />
          <meta
            name='twitter:description'
            content={
              (metaData?.meta_description ?? metaData?.description)?.slice(
                0,
                160,
              ) || ''
            }
          />
          <meta
            name='twitter:image'
            content={`${process.env.NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS}/${metaData?.documents?.file_path}`}
          ></meta>
        </Head>

        {loading ? (
          <div className='flex justify-center h-screen items-center'>
            <CustomLoader />
          </div>
        ) : (
          <div className=''>
            <SchoolDetailsPage schoolData={transformData} loading={loading} />
          </div>
        )}
      </ConfigProvider>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<metaTagProps> = async ({
  query,
  resolvedUrl,
}) => {
  try {
    const { schoolId } = query;
    let metaData;
    if (schoolId) {
      metaData = await getMetaTagSchool(schoolId as string);
    }

    if (!metaData || !metaData.data) {
      throw new Error('Invalid metaData response');
    }

    const canonicalUrlPageUrl = `https://zebralearn.com${resolvedUrl.split('?')[0]}`;

    return {
      props: {
        metaData: metaData.data,
        canonicalUrlPageUrl,
      },
    };
  } catch (error) {
    return {
      props: {
        metaData: null,
        canonicalUrlPageUrl: 'https://zebralearn.com',
      },
    };
  }
};

export default SchoolDetails;
